import i18next from "i18next";
import { BOM_SUPPLIES, PART_GROUP_KINDS, type BomSupply, type PartGroupKind } from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { partGroupKindLabel } from "../manufacturing/partGroupKind";
import { bomSupplyLabel } from "../manufacturing/bomSupply";
import { inventoryPolicyLabel } from "../manufacturing/PartGeneralTab";
import { policyLabel } from "../purchasing/replenishment/MaterialStory";
import { makeConfigGrid, makeConfigPage, type ConfigPageSpec, type LookupRow, type Lookups } from "./makeConfigPage";

/**
 * The reference data the SIGM MFI replacement introduces: units and the things
 * they measure, the shape of the plant, part groups, and the attribute
 * registry.
 *
 * <p>Each is the same shape as the existing config screens — a reference table
 * with inline editing — so each is a spec rather than a page. What differs
 * between them is columns, fields and an endpoint, which is exactly what a
 * spec carries.</p>
 */

/* ---------------------------------------------------------------- types --- */

type UnitDimension = "COUNT" | "LENGTH" | "MASS" | "VOLUME" | "AREA" | "TIME";

interface UnitDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  dimension: UnitDimension;
  /** Null means the unit needs a per-part conversion — a box, a roll, a bag. */
  base_factor: number | null;
  decimals: number;
  is_active: boolean;
  /** Derived, read-only. */
  is_convertible?: boolean;
}

interface MeasurementKindDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  dimension: UnitDimension;
  position: number;
  is_active: boolean;
}

interface SiteDTO {
  id: number;
  code: string;
  name: string;
  address_line: string | null;
  city: string | null;
  is_active: boolean;
}

interface DepartmentDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  /** Null for a department that does not carry production labour. */
  hourly_rate: number | null;
  position: number;
  is_active: boolean;
  is_costed?: boolean;
}

interface OperationDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  department_id: number | null;
  is_setup: boolean;
  is_indirect: boolean;
  is_printed_on_work_order: boolean;
  position: number;
  is_active: boolean;
}

interface StationDTO {
  id: number;
  code: string;
  name: string;
  site_id: number | null;
  operation_id: number | null;
  /** The process this machine performs: a routing step offers the machines of its process. */
  process_id: number | null;
  /** The word SIGM's machine list used for it (ROV for both Rovers): how routings are seeded. */
  legacy_code: string | null;
  is_active: boolean;
}

type CountingUnit = "PART" | "CUTBOARD";

interface ProcessDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  department_id: number | null;
  site_id: number | null;
  counting_unit: CountingUnit;
  has_program: boolean;
  is_routable: boolean;
  position: number;
  is_active: boolean;
  /** The plant's order (Cut 10, Edge and Machining 20, Paint 30, Inspection 40): default step dependencies; equal stages run in parallel. */
  stage: number;
  /** Done outside the plant (Paint): sent, then back. */
  is_external: boolean;
  /** PIECE: one to-do row per piece of a lot. STOCK: works raw stock for one or more lots: its to-do list is cut jobs. */
  works_on: WorksOn;
  /** AUTO: as the steps report progress. COUNTED: the same until the station counts. MANUAL: only what the operator confirms. */
  consumption: Consumption;
}

interface OperatorDTO {
  id: number;
  badge_code: string;
  name: string;
  is_active: boolean;
  /** May go past what a step allows, with a reason (a lead hand, a supervisor). */
  has_override_right: boolean;
}

interface ScrapReasonDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  position: number;
  is_active: boolean;
}

interface StorageLocationDTO {
  id: number;
  warehouse_id: number;
  warehouse_code?: string | null;
  code: string;
  name: string | null;
  position: number;
  is_active: boolean;
  zone: string | null;
  capacity_pallets: number | null;
  note: string | null;
  /** Computed: the lot being packed or packed on it. */
  production_order_id?: number | null;
  production_order_number?: number | null;
  /** Computed: units on it, and of how many products. */
  shelved_units?: number | null;
  product_count?: number;
}

type InventoryPolicy = "TRACKED" | "UNTRACKED";

type ReplenishmentPolicy = "AUTO" | "FIXED" | "ON_DEMAND" | "NOT_PLANNED";
type UsageSource = "RECORDED" | "DERIVED";

interface PartGroupDTO {
  id: number;
  code: string;
  name: string;
  default_inventory_policy: InventoryPolicy;
  is_active: boolean;
  default_replenishment_policy: ReplenishmentPolicy;
  default_usage_source: UsageSource;
  safety_days: number;
  strategic_extra_days: number;
  /** Its parts, when a lot's bill names them, are made in the lot and followed on the floor. */
  is_floor_tracked: boolean;
  /** The process at which orders consume its materials (boards at Cut, hardware at the kit, foam at Foam, cartons at Pack). */
  consumed_at_process_id: number | null;
  /** Where lots draw its materials from when neither the bill line nor the part says. */
  consumption_warehouse_id: number | null;
  /** How a parent gets a part of this group unless the part or the BOM line says otherwise. */
  default_supply: BomSupply;
  /** MATERIAL follows the products that use it, FINISHED its own product, GENERAL is kept by hand. */
  kind: PartGroupKind;
}

type AttributeDataType =
  | "TEXT" | "NUMBER" | "BOOLEAN" | "DATE" | "ENUM" | "PART_REFERENCE" | "DOCUMENT";

interface AttributeDTO {
  id: number;
  entity: "PART" | "PRODUCTION_ORDER" | "SUPPLIER" | "PRODUCT";
  code: string;
  name: string;
  name_fr: string;
  data_type: AttributeDataType;
  unit_id: number | null;
  group_name: string | null;
  position: number;
  is_required: boolean;
  is_searchable: boolean;
  is_system: boolean;
  help_text: string | null;
  /** Set once the attribute has graduated to a real column. */
  promoted_to_column: string | null;
}

/* -------------------------------------------------------------- helpers --- */

/** For labels built outside a component: field options and validation messages, read when shown. */
const tr = (key: string, fallback: string, options?: Record<string, unknown>): string =>
  i18next.t(key, { defaultValue: fallback, ...options }) as string;

type Translate = (key: string, fallback: string) => string;

const text = <T,>(key: keyof T & string, required = false) => ({
  kind: "text" as const,
  get: (row: T) => (row[key] as string | null) ?? "",
  set: (row: T, value: string) => ({ ...row, [key]: value }),
  required,
});

/** A text field whose empty string means null, not "". */
const nullableText = <T,>(key: keyof T & string) => ({
  kind: "text" as const,
  get: (row: T) => (row[key] as string | null) ?? "",
  set: (row: T, value: string) => ({ ...row, [key]: value.trim() === "" ? null : value }),
});

/**
 * A number that must be filled. A cleared box holds NaN until something is
 * typed, so validation can say "enter a number"; writing 0 instead put a 0 in
 * the box the moment it was emptied, and saved it if nobody noticed.
 */
const number = <T,>(key: keyof T & string) => ({
  kind: "number" as const,
  get: (row: T) => {
    const value = row[key] as number;
    return Number.isFinite(value) ? value : "";
  },
  set: (row: T, value: string) => ({ ...row, [key]: value.trim() === "" ? Number.NaN : Number(value) }),
});

/**
 * A number whose blank means "not applicable" rather than zero. Departments
 * that do not carry production labour, and units with no global conversion,
 * both depend on the distinction: zero would claim a rate of nothing.
 */
const nullableNumber = <T,>(key: keyof T & string) => ({
  kind: "number" as const,
  get: (row: T) => (row[key] as number | null) ?? "",
  set: (row: T, value: string) => ({ ...row, [key]: value.trim() === "" ? null : Number(value) }),
});

const check = <T,>(key: keyof T & string) => ({
  kind: "checkbox" as const,
  get: (row: T) => row[key] as boolean,
  set: (row: T, value: boolean) => ({ ...row, [key]: value }),
});

/** A fixed list; `label` translates each value when the editor opens. */
const choice = <T,>(key: keyof T & string, values: readonly string[], label: (value: string) => string = (value) => value) => ({
  kind: "select" as const,
  get: (row: T) => row[key] as string,
  set: (row: T, value: string) => ({ ...row, [key]: value }),
  options: () => values.map((value) => ({ value, label: label(value) })),
  required: true,
});

/** A pick from a lookup, blank for none. */
const reference = <T,>(key: keyof T & string, rows: LookupRow[], label: (row: LookupRow) => string = codeAndName) => ({
  kind: "select" as const,
  get: (row: T) => (row[key] === null || row[key] === undefined ? "" : String(row[key])),
  set: (row: T, value: string) => ({ ...row, [key]: value === "" ? null : Number(value) }),
  options: () => rows.map((row) => ({ value: String(row.id), label: label(row) })),
});

function codeAndName(row: LookupRow): string {
  const name = String(row.name_fr ?? row.name ?? "");
  return name && name !== row.code ? `${String(row.code)} · ${name}` : String(row.code ?? row.id);
}

function nameOf(rows: LookupRow[], id: number | null): string {
  if (id === null) return "";
  const row = rows.find((candidate) => candidate.id === id);
  return row ? codeAndName(row) : `#${id}`;
}

/** Yes/No for a flag, filterable on either. */
const flagColumn = <T,>(c: Parameters<ConfigPageSpec<T & { id: number }>["columns"]>[0], t: Translate, id: keyof T & string, header: string, priority = 5) =>
  c.text(id, (row) => ((row as T)[id] ? t("common.yes", "Yes") : t("common.no", "No")), { header, filter: "select", priority });

/** The Active column every retirable row carries: retiring is how a row in use is taken out of the pickers. */
const activeColumn = <T extends { id: number; is_active: boolean }>(c: Parameters<ConfigPageSpec<T>["columns"]>[0], t: Translate) =>
  c.text("is_active", (row) => (row.is_active ? t("common.yes", "Yes") : t("common.no", "No")), {
    headerKey: "column.active",
    filter: "select",
    priority: 2,
  });

const DIMENSIONS = ["COUNT", "LENGTH", "MASS", "VOLUME", "AREA", "TIME"] as const;
const POLICIES = ["TRACKED", "UNTRACKED"] as const;
const REPLENISHMENT_POLICIES = ["AUTO", "FIXED", "ON_DEMAND", "NOT_PLANNED"] as const;
const USAGE_SOURCES = ["RECORDED", "DERIVED"] as const;
const DATA_TYPES = [
  "TEXT", "NUMBER", "BOOLEAN", "DATE", "ENUM", "PART_REFERENCE", "DOCUMENT",
] as const;

const dimensionLabel = (value: string) => {
  switch (value) {
    case "COUNT": return tr("config.dimension.count", "Count");
    case "LENGTH": return tr("config.dimension.length", "Length");
    case "MASS": return tr("config.dimension.mass", "Mass");
    case "VOLUME": return tr("config.dimension.volume", "Volume");
    case "AREA": return tr("config.dimension.area", "Area");
    case "TIME": return tr("config.dimension.time", "Time");
    default: return value;
  }
};

const usageSourceLabel = (value: string) =>
  value === "DERIVED"
    ? tr("part-replenishment.source.derived", "Derived from finished work")
    : tr("part-replenishment.source.recorded", "Recorded issues");

/* ----------------------------------------------------------- validation --- */

type Errors = Record<string, string>;

/** Every listed text field filled. The database takes "" for a name, so nothing else would stop a nameless row. */
function requireText(row: object, keys: string[], errors: Errors = {}): Errors {
  for (const key of keys) {
    const value = (row as Record<string, unknown>)[key];
    if (typeof value !== "string" || value.trim() === "") errors[key] = tr("validation.required", "Required");
  }
  return errors;
}

/** A whole number within bounds; NaN is a cleared box. */
function requireWhole(errors: Errors, key: string, value: number, minimum: number, maximum = 32767) {
  if (!Number.isFinite(value)) errors[key] = tr("settings.error.required", "Enter a number.");
  else if (!Number.isInteger(value)) errors[key] = tr("settings.error.whole", "Must be a whole number.");
  else if (value < minimum) errors[key] = tr("settings.error.at-least", "Must be at least {{minimum}}.", { minimum });
  else if (value > maximum) errors[key] = tr("config.error.at-most", "Must be at most {{maximum}}.", { maximum });
}

const requireCodeAndNames = (row: object) => requireText(row, ["code", "name", "name_fr"]);
const requireCodeAndName = (row: object) => requireText(row, ["code", "name"]);

const RETIRE_INSTEAD = (t: Translate) =>
  t("config.delete.retire", "One still in use cannot be deleted: untick Active instead, which takes it out of the lists.");

/* ---------------------------------------------------------------- pages --- */

/**
 * Units of measure.
 *
 * A blank conversion is not a missing value: it says the unit converts only
 * per part, which is the honest answer for a box — 500 of one thing, 12 of
 * another. The grid shows "per part" there rather than 0.
 */
export const UnitPage = makeConfigPage<UnitDTO>({
  path: "/unit",
  cacheKey: "unit",
  titleKey: "navigation.item.config-unit",
  titleFallback: "Units of measure",
  view: NexpimView.CONFIG_UNIT,
  alsoInvalidates: [["unit"]],
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 2 }),
    c.text("dimension", (row) => dimensionLabel(row.dimension), { headerKey: "config.dimension", filter: "select", priority: 3 }),
    c.text("base_factor", (row) => (row.base_factor === null ? t("config.per-part", "Per part") : String(row.base_factor)), {
      headerKey: "config.base-factor",
      priority: 4,
    }),
    c.number("decimals", (row) => row.decimals, { headerKey: "config.decimals", priority: 6 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<UnitDTO>("code", true),
      name: text<UnitDTO>("name", true),
      name_fr: text<UnitDTO>("name_fr", true),
      dimension: choice<UnitDTO>("dimension", DIMENSIONS, dimensionLabel),
      base_factor: nullableNumber<UnitDTO>("base_factor"),
      decimals: number<UnitDTO>("decimals"),
      is_active: check<UnitDTO>("is_active"),
    },
    newRow: () => ({
      id: 0, code: "", name: "", name_fr: "", dimension: "COUNT",
      base_factor: null, decimals: 2, is_active: true,
    }),
    validate: (row) => {
      const errors = requireCodeAndNames(row);
      if (row.base_factor !== null && !(Number.isFinite(row.base_factor) && row.base_factor > 0))
        errors.base_factor = tr("config.error.base-factor", "Above 0, or blank for a unit that converts per part.");
      requireWhole(errors, "decimals", row.decimals, 0, 6);
      return errors;
    },
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
});

/** The properties a part can be measured on. A row, so adding one is data entry. */
const measurementKindSpec: ConfigPageSpec<MeasurementKindDTO> = {
  path: "/measurement-kind",
  cacheKey: "measurement-kind",
  titleKey: "navigation.item.config-measurement-kind",
  titleFallback: "Measurement kinds",
  view: NexpimView.CONFIG_UNIT,
  alsoInvalidates: [["measurement-kind"]],
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 2 }),
    c.text("dimension", (row) => dimensionLabel(row.dimension), { headerKey: "config.dimension", filter: "select", priority: 3 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<MeasurementKindDTO>("code", true),
      name: text<MeasurementKindDTO>("name", true),
      name_fr: text<MeasurementKindDTO>("name_fr", true),
      dimension: choice<MeasurementKindDTO>("dimension", DIMENSIONS, dimensionLabel),
      is_active: check<MeasurementKindDTO>("is_active"),
    },
    newRow: () => ({
      id: 0, code: "", name: "", name_fr: "", dimension: "LENGTH", position: 1000, is_active: true,
    }),
    validate: requireCodeAndNames,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
};
export const MeasurementKindPage = makeConfigPage(measurementKindSpec);

/** Where the company operates. A production order moves between these. */
export const SitePage = makeConfigPage<SiteDTO>({
  path: "/site",
  cacheKey: "site",
  titleKey: "navigation.item.config-site",
  titleFallback: "Sites",
  view: NexpimView.CONFIG_PLANT,
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("address_line", (row) => row.address_line ?? "", { headerKey: "column.address", priority: 4 }),
    c.text("city", (row) => row.city ?? "", { headerKey: "config.city", priority: 3 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<SiteDTO>("code", true),
      name: text<SiteDTO>("name", true),
      address_line: nullableText<SiteDTO>("address_line"),
      city: nullableText<SiteDTO>("city"),
      is_active: check<SiteDTO>("is_active"),
    },
    newRow: () => ({ id: 0, code: "", name: "", address_line: null, city: null, is_active: true }),
    validate: requireCodeAndName,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
});

/**
 * Departments and their labour rate.
 *
 * A blank rate means the department does not carry production labour, which is
 * true of five of the eleven. Zero would say it carries labour at no cost.
 */
const departmentSpec: ConfigPageSpec<DepartmentDTO> = {
  path: "/department",
  cacheKey: "department",
  titleKey: "navigation.item.config-department",
  titleFallback: "Departments",
  view: NexpimView.CONFIG_PLANT,
  alsoInvalidates: [["department"]],
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 2 }),
    c.text(
      "hourly_rate",
      (row) => (row.hourly_rate === null ? t("config.not-costed", "No labour") : row.hourly_rate.toFixed(2)),
      { headerKey: "config.hourly-rate", priority: 3 },
    ),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<DepartmentDTO>("code", true),
      name: text<DepartmentDTO>("name", true),
      name_fr: text<DepartmentDTO>("name_fr", true),
      hourly_rate: { ...nullableNumber<DepartmentDTO>("hourly_rate"), placeholder: tr("config.not-costed", "No labour") },
      is_active: check<DepartmentDTO>("is_active"),
    },
    newRow: () => ({
      id: 0, code: "", name: "", name_fr: "", hourly_rate: null, position: 1000, is_active: true,
    }),
    validate: (row) => {
      const errors = requireCodeAndNames(row);
      if (row.hourly_rate !== null && !(Number.isFinite(row.hourly_rate) && row.hourly_rate >= 0))
        errors.hourly_rate = tr("settings.error.at-least", "Must be at least {{minimum}}.", { minimum: 0 });
      return errors;
    },
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
};
export const DepartmentPage = makeConfigPage(departmentSpec);

/** The work codes the floor follows. 168 of them, imported from SIGM. */
const operationSpec: ConfigPageSpec<OperationDTO> = {
  path: "/operation",
  cacheKey: "operation",
  titleKey: "navigation.item.config-operation",
  titleFallback: "Operations",
  view: NexpimView.CONFIG_PLANT,
  lookups: { departments: { path: "/department", cacheKey: "department" } },
  alsoInvalidates: [["operation"]],
  columns: (c, t, lookups: Lookups) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 4 }),
    // Its department is what prices its labour.
    c.text("department_id", (row) => nameOf(lookups.departments ?? [], row.department_id), {
      headerKey: "form.field.department",
      filter: "select",
      priority: 2,
    }),
    flagColumn<OperationDTO>(c, t, "is_setup", t("config.setup", "Setup")),
    flagColumn<OperationDTO>(c, t, "is_indirect", t("config.indirect", "Indirect")),
    flagColumn<OperationDTO>(c, t, "is_printed_on_work_order", t("config.printed", "On work order"), 6),
    activeColumn(c, t),
  ],
  editor: {
    fields: (lookups) => ({
      code: text<OperationDTO>("code", true),
      name: text<OperationDTO>("name", true),
      name_fr: text<OperationDTO>("name_fr", true),
      department_id: reference<OperationDTO>("department_id", lookups.departments ?? []),
      is_setup: check<OperationDTO>("is_setup"),
      is_indirect: check<OperationDTO>("is_indirect"),
      is_printed_on_work_order: check<OperationDTO>("is_printed_on_work_order"),
      is_active: check<OperationDTO>("is_active"),
    }),
    newRow: () => ({
      id: 0, code: "", name: "", name_fr: "", department_id: null,
      is_setup: false, is_indirect: false, is_printed_on_work_order: true,
      position: 10000, is_active: true,
    }),
    validate: requireCodeAndNames,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
};
export const OperationPage = makeConfigPage(operationSpec);

const COUNTING_UNITS = ["PART", "CUTBOARD"] as const;
const WORKS_ON = ["PIECE", "STOCK"] as const;
type WorksOn = (typeof WORKS_ON)[number];
const CONSUMPTIONS = ["AUTO", "COUNTED", "MANUAL"] as const;
type Consumption = (typeof CONSUMPTIONS)[number];

const countingUnitLabel = (value: string) =>
  value === "CUTBOARD" ? tr("config.counting.cutboard", "Cutboards") : tr("config.counting.part", "Parts");

/**
 * What is done to a part on the floor: cut, edge, machine, paint, inspect,
 * pack. A routing lists them in order; a lot counts each one's quantities, in
 * parts or in cutboards (a small part is cut and edged nested on a board). The
 * codes are what the documents and the boards rely on: retire a process
 * rather than renaming its code.
 */
export const ProcessPage = makeConfigPage<ProcessDTO>({
  path: "/manufacturing/process",
  cacheKey: "process",
  titleKey: "navigation.item.config-process",
  titleFallback: "Processes",
  view: NexpimView.CONFIG_PLANT,
  lookups: {
    departments: { path: "/department", cacheKey: "department" },
    sites: { path: "/site", cacheKey: "site" },
  },
  alsoInvalidates: [["process"]],
  columns: (c, t, lookups) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 2 }),
    c.text("department_id", (row) => nameOf(lookups.departments ?? [], row.department_id), {
      headerKey: "form.field.department",
      filter: "select",
      priority: 3,
    }),
    c.text("site_id", (row) => nameOf(lookups.sites ?? [], row.site_id), {
      header: t("config.site", "Site"),
      filter: "select",
      priority: 3,
    }),
    c.text("counting_unit", (row) => countingUnitLabel(row.counting_unit), {
      header: t("config.counting-unit", "Counts"),
      filter: "select",
      priority: 3,
    }),
    flagColumn<ProcessDTO>(c, t, "has_program", t("config.has-program", "Machine program"), 4),
    flagColumn<ProcessDTO>(c, t, "is_routable", t("config.routable", "In routings"), 4),
    c.number("stage", (row) => row.stage, { header: t("config.stage", "Stage"), priority: 3 }),
    flagColumn<ProcessDTO>(c, t, "is_external", t("config.external", "Done outside"), 4),
    c.text("works_on", (row) => worksOnLabel(t, row.works_on), {
      header: t("config.works-on", "Works on"),
      filter: "select",
      priority: 3,
    }),
    c.text("consumption", (row) => consumptionLabel(t, row.consumption), {
      header: t("config.consumption", "Materials used"),
      filter: "select",
      priority: 3,
    }),
    activeColumn(c, t),
  ],
  editor: {
    fields: (lookups) => ({
      code: text<ProcessDTO>("code", true),
      name: text<ProcessDTO>("name", true),
      name_fr: text<ProcessDTO>("name_fr", true),
      department_id: reference<ProcessDTO>("department_id", lookups.departments ?? []),
      site_id: reference<ProcessDTO>("site_id", lookups.sites ?? []),
      counting_unit: choice<ProcessDTO>("counting_unit", COUNTING_UNITS, countingUnitLabel),
      has_program: check<ProcessDTO>("has_program"),
      is_routable: check<ProcessDTO>("is_routable"),
      stage: number<ProcessDTO>("stage"),
      is_external: check<ProcessDTO>("is_external"),
      works_on: choice<ProcessDTO>("works_on", WORKS_ON, (value) => worksOnLabel(tr, value)),
      consumption: choice<ProcessDTO>("consumption", CONSUMPTIONS, (value) => consumptionLabel(tr, value)),
      is_active: check<ProcessDTO>("is_active"),
    }),
    newRow: () => ({
      id: 0, code: "", name: "", name_fr: "", department_id: null, site_id: null, counting_unit: "PART",
      has_program: false, is_routable: true, position: 1000, is_active: true, stage: 50, is_external: false,
      works_on: "PIECE", consumption: "COUNTED",
    }),
    validate: requireCodeAndNames,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
});

/**
 * The plant's machines and benches. Each performs one process; a routing step
 * names the ones allowed, and a station kiosk reports as one. The legacy code
 * is the word SIGM's machine list used for it (ROV covers both Rovers): the
 * routing seeding reads the parts' free text through it.
 */
export const StationPage = makeConfigPage<StationDTO>({
  path: "/station",
  cacheKey: "station",
  titleKey: "navigation.item.config-station",
  titleFallback: "Machines",
  view: NexpimView.CONFIG_PLANT,
  lookups: {
    sites: { path: "/site", cacheKey: "site" },
    operations: { path: "/operation", cacheKey: "operation" },
    processes: { path: "/manufacturing/process", cacheKey: "process" },
  },
  alsoInvalidates: [["station"]],
  columns: (c, t, lookups) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("process_id", (row) => nameOf(lookups.processes ?? [], row.process_id), {
      header: t("config.process", "Process"),
      filter: "select",
      priority: 2,
    }),
    c.text("legacy_code", (row) => row.legacy_code ?? "", {
      header: t("config.legacy-code", "SIGM code"),
      priority: 3,
    }),
    c.text("site_id", (row) => nameOf(lookups.sites ?? [], row.site_id), {
      header: t("config.site", "Site"),
      filter: "select",
      priority: 3,
    }),
    c.text("operation_id", (row) => nameOf(lookups.operations ?? [], row.operation_id), {
      header: t("config.operation", "Operation"),
      filter: "select",
      priority: 4,
    }),
    activeColumn(c, t),
  ],
  editor: {
    fields: (lookups) => ({
      code: text<StationDTO>("code", true),
      name: text<StationDTO>("name", true),
      process_id: reference<StationDTO>("process_id", lookups.processes ?? []),
      legacy_code: nullableText<StationDTO>("legacy_code"),
      site_id: reference<StationDTO>("site_id", lookups.sites ?? []),
      operation_id: reference<StationDTO>("operation_id", lookups.operations ?? []),
      is_active: check<StationDTO>("is_active"),
    }),
    newRow: () => ({
      id: 0, code: "", name: "", site_id: null, operation_id: null, process_id: null, legacy_code: null, is_active: true,
    }),
    validate: requireCodeAndName,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
});

/**
 * Part groups.
 *
 * The default stock policy is set here rather than per part, because that is
 * the level the decision is actually taken at: a group of printed labels is
 * expensed, and the handful of exceptions are overridden on the part.
 *
 * <p>The same goes for replenishment. This grid is the "safety days by group"
 * table: how its purchased parts are replenished (AUTO follows usage, FIXED
 * keeps a typed minimum and maximum, ON_DEMAND buys only for open production
 * orders, NOT_PLANNED is left to people), whether usage is what left the
 * shelf or what finished work should have used, how many days of usage are
 * kept as safety stock, and how many more for parts that feed the products
 * the pipeline protects. A part overrides any of them on its Replenishment
 * tab; the monthly review suggests changes here.</p>
 */
export const PartGroupPage = makeConfigPage<PartGroupDTO>({
  path: "/manufacturing/part-group",
  cacheKey: "part-group",
  titleKey: "navigation.item.config-part-group",
  titleFallback: "Part groups",
  view: NexpimView.CONFIG_PART_GROUP,
  // The part screens and the stock count cache the groups under their own keys.
  alsoInvalidates: [["manufacturing-part-group"], ["part-group"]],
  lookups: {
    processes: { path: "/manufacturing/process", cacheKey: "process" },
    warehouses: { path: "/warehouse", cacheKey: "warehouse" },
  },
  columns: (c, t, lookups) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("kind", (row) => partGroupKindLabel(row.kind, t), {
      header: t("config.group-kind", "Kind"),
      filter: "select",
      priority: 2,
    }),
    c.text("default_inventory_policy", (row) => inventoryPolicyLabel(row.default_inventory_policy, t), {
      headerKey: "config.stock-policy",
      filter: "select",
      priority: 3,
    }),
    c.text("default_replenishment_policy", (row) => policyLabel(row.default_replenishment_policy, t), {
      headerKey: "config.replenishment-policy",
      filter: "select",
      priority: 3,
    }),
    c.text("default_usage_source", (row) => usageSourceLabel(row.default_usage_source), {
      headerKey: "config.usage-source",
      filter: "select",
    }),
    c.number("safety_days", (row) => row.safety_days, { headerKey: "config.safety-days", filter: "range", priority: 3 }),
    c.number("strategic_extra_days", (row) => row.strategic_extra_days, {
      headerKey: "config.strategic-extra-days",
      filter: "range",
    }),
    c.text("default_supply", (row) => bomSupplyLabel(row.default_supply, t), {
      header: t("config.default-supply", "Supply"),
      filter: "select",
      priority: 3,
    }),
    flagColumn<PartGroupDTO>(c, t, "is_floor_tracked", t("config.floor-tracked", "On the floor"), 3),
    c.text("consumed_at_process_id", (row) => nameOf(lookups.processes ?? [], row.consumed_at_process_id), {
      header: t("config.consumed-at", "Consumed at"),
      filter: "select",
      priority: 4,
    }),
    c.text("consumption_warehouse_id", (row) => nameOf(lookups.warehouses ?? [], row.consumption_warehouse_id), {
      header: t("config.drawn-from", "Drawn from"),
      filter: "select",
      priority: 4,
    }),
    activeColumn(c, t),
  ],
  editor: {
    fields: (lookups) => ({
      code: text<PartGroupDTO>("code", true),
      name: text<PartGroupDTO>("name", true),
      kind: choice<PartGroupDTO>("kind", PART_GROUP_KINDS, (value) => partGroupKindLabel(value as PartGroupKind, tr)),
      default_inventory_policy: choice<PartGroupDTO>("default_inventory_policy", POLICIES, (value) => inventoryPolicyLabel(value, tr)),
      default_replenishment_policy: choice<PartGroupDTO>("default_replenishment_policy", REPLENISHMENT_POLICIES, (value) =>
        policyLabel(value, tr),
      ),
      default_usage_source: choice<PartGroupDTO>("default_usage_source", USAGE_SOURCES, usageSourceLabel),
      safety_days: number<PartGroupDTO>("safety_days"),
      strategic_extra_days: number<PartGroupDTO>("strategic_extra_days"),
      default_supply: choice<PartGroupDTO>("default_supply", BOM_SUPPLIES, (value) => bomSupplyLabel(value as BomSupply, tr)),
      is_floor_tracked: check<PartGroupDTO>("is_floor_tracked"),
      consumed_at_process_id: reference<PartGroupDTO>("consumed_at_process_id", lookups.processes ?? []),
      // Never a distribution warehouse: that stock is Nexera's.
      consumption_warehouse_id: reference<PartGroupDTO>(
        "consumption_warehouse_id",
        (lookups.warehouses ?? []).filter((warehouse) => warehouse.kind !== "DISTRIBUTION"),
      ),
      is_active: check<PartGroupDTO>("is_active"),
    }),
    newRow: () => ({
      id: 0,
      code: "",
      name: "",
      default_inventory_policy: "TRACKED",
      is_active: true,
      default_replenishment_policy: "AUTO",
      default_usage_source: "RECORDED",
      safety_days: 7,
      strategic_extra_days: 3,
      is_floor_tracked: false,
      default_supply: "PHANTOM",
      consumed_at_process_id: null,
      consumption_warehouse_id: null,
      kind: "MATERIAL",
    }),
    validate: (row) => {
      const errors = requireCodeAndName(row);
      requireWhole(errors, "safety_days", row.safety_days, 0);
      requireWhole(errors, "strategic_extra_days", row.strategic_extra_days, 0);
      return errors;
    },
    unique: ["code"],
    canDelete: true,
    deleteMessage: (t) =>
      t(
        "config.delete.part-group",
        "A group that still has parts cannot be deleted: untick Active instead. Attributes limited to this group lose it.",
      ),
  },
});

/**
 * The attribute registry: fields people can add without a migration.
 *
 * Anything code decides on is a column instead, which is why `promoted_to_column`
 * is shown — an attribute carrying one has graduated and is kept only so the
 * history is visible.
 */
const attributeSpec: ConfigPageSpec<AttributeDTO> = {
  path: "/attribute",
  cacheKey: "attribute",
  titleKey: "navigation.item.config-attribute",
  titleFallback: "Part attributes",
  view: NexpimView.CONFIG_ATTRIBUTE,
  lookups: { units: { path: "/unit", cacheKey: "unit" } },
  alsoInvalidates: [["attribute"]],
  columns: (c, t, lookups) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 3 }),
    c.text("data_type", (row) => row.data_type, { headerKey: "config.data-type", filter: "select", priority: 3 }),
    c.text("unit_id", (row) => nameOf(lookups.units ?? [], row.unit_id), { header: t("config.unit", "Unit"), priority: 5 }),
    c.text("group_name", (row) => row.group_name ?? "", { headerKey: "config.group", priority: 4 }),
    c.text("help_text", (row) => row.help_text ?? "", { header: t("config.help-text", "Help text"), priority: 7 }),
    flagColumn<AttributeDTO>(c, t, "is_required", t("config.required", "Required")),
    flagColumn<AttributeDTO>(c, t, "is_searchable", t("config.searchable", "Searchable")),
    c.text("promoted_to_column", (row) => row.promoted_to_column ?? "", {
      headerKey: "config.promoted-to",
      priority: 6,
    }),
  ],
  editor: {
    fields: (lookups) => ({
      code: text<AttributeDTO>("code", true),
      name: text<AttributeDTO>("name", true),
      name_fr: text<AttributeDTO>("name_fr", true),
      // A unit only means something on a number; the database refuses one anywhere else.
      data_type: {
        ...choice<AttributeDTO>("data_type", DATA_TYPES),
        set: (row: AttributeDTO, value: string) => ({
          ...row,
          data_type: value as AttributeDataType,
          unit_id: value === "NUMBER" ? row.unit_id : null,
        }),
      },
      unit_id: {
        ...reference<AttributeDTO>("unit_id", lookups.units ?? []),
        options: (row: AttributeDTO) =>
          row.data_type === "NUMBER"
            ? (lookups.units ?? []).map((unit) => ({ value: String(unit.id), label: codeAndName(unit) }))
            : [],
      },
      group_name: nullableText<AttributeDTO>("group_name"),
      help_text: nullableText<AttributeDTO>("help_text"),
      is_required: check<AttributeDTO>("is_required"),
      is_searchable: check<AttributeDTO>("is_searchable"),
    }),
    newRow: () => ({
      id: 0, entity: "PART", code: "", name: "", name_fr: "", data_type: "TEXT",
      unit_id: null, group_name: null, position: 1000,
      is_required: false, is_searchable: true, is_system: false,
      help_text: null, promoted_to_column: null,
    }),
    validate: requireCodeAndNames,
    unique: ["code"],
    canDelete: true,
    deleteMessage: (t) => t("config.delete.attribute", "Every value recorded for it on parts is deleted with it."),
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
};
export const AttributePage = makeConfigPage(attributeSpec);

/* ------------------------------------------------- stock adjustment reasons --- */

interface StockAdjustmentReasonDTO {
  id: number;
  code: string;
  name: string;
  name_fr: string;
  position: number;
  is_active: boolean;
}

/**
 * Why stock is corrected. Required on every adjustment and count, so each one
 * answers "why is this part short" on its own. Retire a reason rather than
 * delete it: past adjustments keep the one they were posted with.
 */
export const StockAdjustmentReasonPage = makeConfigPage<StockAdjustmentReasonDTO>({
  path: "/manufacturing/stock-adjustment-reason",
  cacheKey: "stock-adjustment-reason",
  titleKey: "navigation.item.config-stock-reason",
  titleFallback: "Stock adjustment reasons",
  view: NexpimView.MANUFACTURING_STOCK,
  alsoInvalidates: [["stock-adjustment-reason"]],
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 3 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<StockAdjustmentReasonDTO>("code", true),
      name: text<StockAdjustmentReasonDTO>("name", true),
      name_fr: text<StockAdjustmentReasonDTO>("name_fr", true),
      is_active: check<StockAdjustmentReasonDTO>("is_active"),
    },
    newRow: () => ({ id: 0, code: "", name: "", name_fr: "", position: 1000, is_active: true }),
    validate: requireCodeAndNames,
    unique: ["code"],
  },
  order: {
    get: (row) => row.position,
    set: (row, position) => ({ ...row, position }),
  },
});

/* ------------------------------------------------------ shop-floor setup --- */

/**
 * The people on the floor, by the badge a kiosk scans. Not accounts: a kiosk
 * is logged in as its station, and the badge only says who did the work. An
 * operator who leaves is made inactive (the badge is refused) and stays on the
 * events they reported.
 */
export const OperatorGrid = makeConfigGrid<OperatorDTO>({
  path: "/production/operator",
  cacheKey: "operator",
  titleKey: "shop-floor-setup.operators",
  titleFallback: "Operators",
  view: NexpimView.CONFIG_SHOP_FLOOR,
  columns: (c, t) => [
    c.text("badge_code", (row) => row.badge_code, { header: t("shop-floor-setup.badge", "Badge"), priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
    flagColumn<OperatorDTO>(c, t, "has_override_right", t("shop-floor-setup.may-override", "May override"), 2),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      badge_code: text<OperatorDTO>("badge_code", true),
      name: text<OperatorDTO>("name", true),
      has_override_right: check<OperatorDTO>("has_override_right"),
      is_active: check<OperatorDTO>("is_active"),
    },
    newRow: () => ({ id: 0, badge_code: "", name: "", is_active: true, has_override_right: false }),
    validate: (row) => requireText(row, ["badge_code", "name"]),
    unique: ["badge_code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
});

/** Why parts are scrapped at a step: the list a kiosk offers with a scrap quantity. */
export const ScrapReasonGrid = makeConfigGrid<ScrapReasonDTO>({
  path: "/production/scrap-reason",
  cacheKey: "scrap-reason",
  titleKey: "shop-floor-setup.scrap-reasons",
  titleFallback: "Scrap reasons",
  view: NexpimView.CONFIG_SHOP_FLOOR,
  columns: (c, t) => [
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name, { headerKey: "column.name", priority: 2 }),
    c.text("name_fr", (row) => row.name_fr, { headerKey: "column.name-fr", priority: 2 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: {
      code: text<ScrapReasonDTO>("code", true),
      name: text<ScrapReasonDTO>("name", true),
      name_fr: text<ScrapReasonDTO>("name_fr", true),
      is_active: check<ScrapReasonDTO>("is_active"),
    },
    newRow: () => ({ id: 0, code: "", name: "", name_fr: "", position: 1000, is_active: true }),
    validate: requireCodeAndNames,
    unique: ["code"],
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
});

/**
 * Places inside a warehouse: Terrebonne's numbered shelves, where a packed lot
 * waits, with their zone and capacity. What is on them is the Shelves page's.
 */
export const StorageLocationGrid = makeConfigGrid<StorageLocationDTO>({
  path: "/manufacturing/storage-location",
  cacheKey: "storage-location",
  titleKey: "shop-floor-setup.locations",
  titleFallback: "Storage locations",
  view: NexpimView.CONFIG_SHOP_FLOOR,
  lookups: { warehouses: { path: "/warehouse", cacheKey: "warehouse" } },
  columns: (c, t, lookups) => [
    c.text("warehouse_id", (row) => row.warehouse_code ?? nameOf(lookups.warehouses ?? [], row.warehouse_id), {
      header: t("config.warehouse", "Warehouse"),
      filter: "select",
      priority: 1,
    }),
    c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
    c.text("name", (row) => row.name ?? "", { headerKey: "column.name", priority: 3 }),
    c.text("zone", (row) => row.zone ?? "", { header: t("shelves.zone", "Zone"), filter: "select", priority: 2 }),
    c.text("capacity_pallets", (row) => (row.capacity_pallets == null ? "" : String(row.capacity_pallets)), {
      header: t("shelves.capacity", "Pallets"),
      priority: 3,
    }),
    c.text("shelved_units", (row) => (row.shelved_units ? String(Number(row.shelved_units)) : ""), {
      header: t("shelves.units", "Units on it"),
      priority: 2,
    }),
    c.text("production_order_number", (row) => (row.production_order_number == null ? "" : String(row.production_order_number)), {
      header: t("shop-floor-setup.lot-on-shelf", "Lot on it"),
      priority: 2,
    }),
    c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    activeColumn(c, t),
  ],
  editor: {
    fields: (lookups) => ({
      // Chosen when adding; a location does not move to another warehouse after.
      warehouse_id: {
        kind: "select" as const,
        get: (row: StorageLocationDTO) => (row.warehouse_id ? String(row.warehouse_id) : ""),
        set: (row: StorageLocationDTO, value: string) => ({ ...row, warehouse_id: Number(value) }),
        options: (row: StorageLocationDTO) =>
          (lookups.warehouses ?? [])
            .filter((warehouse) => row.id === 0 || warehouse.id === row.warehouse_id)
            .map((warehouse) => ({ value: String(warehouse.id), label: codeAndName(warehouse) })),
        required: true,
      },
      code: text<StorageLocationDTO>("code", true),
      name: nullableText<StorageLocationDTO>("name"),
      zone: nullableText<StorageLocationDTO>("zone"),
      capacity_pallets: nullableNumber<StorageLocationDTO>("capacity_pallets"),
      note: nullableText<StorageLocationDTO>("note"),
      is_active: check<StorageLocationDTO>("is_active"),
    }),
    newRow: () => ({
      id: 0,
      warehouse_id: 0,
      code: "",
      name: null,
      position: 1000,
      is_active: true,
      zone: null,
      capacity_pallets: null,
      note: null,
    }),
    validate: (row) => {
      const errors = requireText(row, ["code"]);
      if (!(row.warehouse_id > 0)) errors.warehouse_id = tr("validation.required", "Required");
      if (row.capacity_pallets != null && !(Number.isInteger(row.capacity_pallets) && row.capacity_pallets > 0)) {
        errors.capacity_pallets = tr("shelves.error.capacity", "A whole number of pallets, above zero.");
      }
      return errors;
    },
    canDelete: true,
    deleteMessage: RETIRE_INSTEAD,
  },
  order: { get: (row) => row.position, set: (row, position) => ({ ...row, position }) },
});

/** PIECE / STOCK, as a process's to-do list is built (0074). */
function worksOnLabel(t: (key: string, fallback: string) => string, value: string): string {
  return value === "STOCK" ? t("config.works-on.stock", "Raw stock: cut jobs") : t("config.works-on.piece", "Pieces: one row each");
}

/** AUTO / COUNTED / MANUAL, as a process uses its materials (0074). */
function consumptionLabel(t: (key: string, fallback: string) => string, value: string): string {
  switch (value) {
    case "AUTO":
      return t("config.consumption.auto", "As the work is reported");
    case "MANUAL":
      return t("config.consumption.manual", "Manual: what the operator confirms");
    default:
      return t("config.consumption.counted", "As reported, until counted");
  }
}
